import { useEffect } from "react";
import { useFiscalStore, type UploadRecord } from "@/store/fiscal-store";
import { fmtBytes, fmtDur, fmtNum } from "@/lib/format";
import {
  CheckCircle2, XCircle, FileArchive, Clock, BarChart3, Trash2, RefreshCcw
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

function RecordRow({ r }: { r: UploadRecord }) {
  const { removeUploadRecord } = useFiscalStore();
  const isOk = r.status === "success";
  
  const onDelete = async () => {
    if (!window.confirm(`Excluir lote ${r.id} e todas as ${r.totalNotas} notas associadas?`)) return;
    try {
      toast.info("Removendo lote e reindexando dados...");
      await removeUploadRecord(r.id);
      toast.success("Lote excluído com sucesso.");
    } catch (err) {
      toast.error("Erro ao excluir lote.");
    }
  };

  return (
    <tr className="border-t border-border hover:bg-primary/5 transition-colors">
      <td className="p-3">
        <div className="flex items-center gap-2">
          {isOk ? <CheckCircle2 className="size-4 text-emerald-500 shrink-0" /> : <XCircle className="size-4 text-red-500 shrink-0" />}
          <div>
            <div className="font-medium text-sm truncate max-w-[180px] text-foreground">{r.fileName}</div>
            <div className="text-[10px] text-muted-foreground uppercase font-bold tracking-tighter">ID: {r.id.split("-")[0]}</div>
          </div>
        </div>
      </td>
      <td className="p-3">
        <div className="font-bold text-[11px] text-foreground uppercase truncate max-w-[150px]">{r.entidadeNome || "—"}</div>
        <div className="text-[9px] text-muted-foreground tabular-nums">{r.entidadeCnpj || "—"}</div>
      </td>
      <td className="p-3 text-sm text-muted-foreground">{new Date(r.timestamp).toLocaleString("pt-BR")}</td>
      <td className="p-3 text-right tabular-nums font-bold text-foreground">{fmtNum(r.totalNotas)}</td>
      <td className="p-3 text-right tabular-nums">
        <span className={r.totalDivergencias > 0 ? "text-red-500 font-bold" : "text-emerald-500 font-medium"}>{fmtNum(r.totalDivergencias)}</span>
      </td>
      <td className="p-3 text-right text-muted-foreground text-sm tabular-nums">{fmtDur(r.duracaoMs)}</td>
      <td className="p-3">
        <span className={`text-[10px] px-2 py-1 rounded-md font-semibold tracking-wide ${isOk ? "bg-emerald-500/15 text-emerald-500 ring-1 ring-emerald-500/30" : "bg-red-500/15 text-red-500 ring-1 ring-red-500/30"}`}>
          {isOk ? "SUCESSO" : "ERRO"}
        </span>
      </td>
      <td className="p-3 text-right">
        <Button variant="ghost" size="icon" className="size-8 text-muted-foreground hover:text-red-500 hover:bg-red-500/10 rounded-lg" onClick={onDelete}>
          <Trash2 className="size-4" />
        </Button>
      </td>
    </tr>
  );
}

export default function ImportacoesPage() {
  const { uploadHistory, purgeAllData } = useFiscalStore();
  const navigate = useNavigate();
  
  const onPurgeAll = async () => {
    if (!window.confirm("CUIDADO: Esta ação irá apagar TODOS os dados do banco local (notas, itens e logs). Deseja continuar?")) return;
    if (!window.confirm("CONFIRMAÇÃO FINAL: Todos os dados serão perdidos permanentemente.")) return;
    
    try {
      toast.info("Limpando banco de dados local...");
      const { clearLocalData } = await import("@/lib/local/db");
      await clearLocalData();
      purgeAllData();
      toast.success("Banco de dados resetado com sucesso.");
    } catch (err) {
      toast.error("Erro ao limpar dados.");
    }
  };

  useEffect(() => { document.title = "Importações — DataGrid XML"; }, []);

  const kpis = [
    { label: "Uploads realizados", value: fmtNum(uploadHistory.length), icon: FileArchive },
    { label: "Total de notas", value: fmtNum(uploadHistory.reduce((s, r) => s + r.totalNotas, 0)), icon: BarChart3 },
    { label: "Indícios", value: fmtNum(uploadHistory.reduce((s, r) => s + r.totalDivergencias, 0)), icon: XCircle },
    { label: "Tempo total", value: fmtDur(uploadHistory.reduce((s, r) => s + r.duracaoMs, 0)), icon: Clock },
  ];

  return (
    <div className="px-6 py-6 max-w-7xl mx-auto space-y-6 animate-in fade-in duration-500">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-black tracking-tight text-foreground">Importações</h1>
          <p className="text-sm text-muted-foreground mt-1.5 font-medium">Histórico de uploads realizados e processados.</p>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={onPurgeAll} variant="outline" className="border-red-500/30 text-red-500 hover:bg-red-500 hover:text-white gap-2 btn-glass">
            <RefreshCcw className="size-4" /> Limpar Tudo
          </Button>
          <Button onClick={() => navigate("/upload")} className="btn-primary gap-2">
            <FileArchive className="size-4" /> Novo Upload
          </Button>
        </div>
      </header>

      <section>
        {uploadHistory.length > 0 && (
          <div className="grid md:grid-cols-4 gap-6 mb-6">
            {kpis.map((kpi) => (
              <div key={kpi.label} className="glass-card rounded-2xl p-6 relative overflow-hidden group">
                <div className="flex items-center justify-between relative z-10">
                  <div className="text-[10px] uppercase tracking-widest font-black text-muted-foreground">{kpi.label}</div>
                  <div className="size-8 rounded-lg flex items-center justify-center bg-primary/10 text-primary">
                    <kpi.icon className="size-4" />
                  </div>
                </div>
                <div className="mt-4 text-3xl font-black tabular-nums text-foreground relative z-10">{kpi.value}</div>
              </div>
            ))}
          </div>
        )}
        <div className="glass-card rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-primary/5">
                <tr>
                  <th className="text-left p-4 font-bold text-[11px] uppercase text-muted-foreground tracking-wider border-b border-border">Arquivo</th>
                  <th className="text-left p-4 font-bold text-[11px] uppercase text-muted-foreground tracking-wider border-b border-border">Entidade / Inspeção</th>
                  <th className="text-left p-4 font-bold text-[11px] uppercase text-muted-foreground tracking-wider border-b border-border">Data / Hora</th>
                  <th className="text-right p-4 font-bold text-[11px] uppercase text-muted-foreground tracking-wider border-b border-border">Notas</th>
                  <th className="text-right p-4 font-bold text-[11px] uppercase text-muted-foreground tracking-wider border-b border-border">Indícios</th>
                  <th className="text-right p-4 font-bold text-[11px] uppercase text-muted-foreground tracking-wider border-b border-border">Duração</th>
                  <th className="text-left p-4 font-bold text-[11px] uppercase text-muted-foreground tracking-wider border-b border-border">Status</th>
                  <th className="text-right p-4 font-bold text-[11px] uppercase text-muted-foreground tracking-wider border-b border-border">Ações</th>
                </tr>
              </thead>
              <tbody>
                {uploadHistory.map((r) => <RecordRow key={r.id} r={r} />)}
                {uploadHistory.length === 0 && (
                  <tr><td colSpan={8} className="p-16 text-center text-muted-foreground">
                    <FileArchive className="size-12 mx-auto mb-4 opacity-20 text-primary" />
                    <div className="font-bold text-lg text-foreground">Nenhum upload realizado</div>
                    <div className="text-sm mt-1">Os arquivos processados nesta sessão aparecerão aqui.</div>
                    <Button variant="outline" className="mt-6 gap-2 btn-glass" onClick={() => navigate("/upload")}>
                      <FileArchive className="size-4" /> Ir para Upload
                    </Button>
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}

